import type { ExpectedPaymentReason, LiquidityCategory, LiquidityGranularity, LiquiditySeries, PlanningItem } from "@kundeninfo/api-client";

/**
 * TEXTE DER LIQUIDITÄT (Spec „Oberfläche › Texte“): EINE Stelle für alle Anwendertexte des Bereichs, Schlüssel englisch.
 * Kategorien, Gründe, Quellen und Verdichtungen sind `Record`s über den Klienttyp: Der Typprüfer meldet eine fehlende.
 * Die Sätze der API stehen in der Domäne (`LIQUIDITY_TEXT`).
 */

export type LiquiditySource = LiquiditySeries["missingSources"][number];
type PositionGroupKey = LiquiditySeries["positions"][number]["group"];
type PositionNote = LiquiditySeries["positions"][number]["notes"][number];
export type DraftCounts = { off: number; on: number; frozen: number; live: number };

const count = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;
const positions = (n: number): string => count(n, "Position", "Positionen");
const paidInvoices = (n: number): string => count(n, "bezahlten Rechnung", "bezahlten Rechnungen");
const EXPECTED_PAYMENT = "Zahlungseingang erwartet";

const CATEGORY: Readonly<Record<LiquidityCategory, string>> = {
  INVOICES: "Offene Rechnungen", PLANNED_CHARGES: "Zahlpläne", PLANNED_REVENUE: "Geplante Einnahmen",
  SUPPLIER_CONTRACTS: "Lieferverträge", INCOMING_INVOICES: "Eingangsrechnungen ohne Vertrag", PERSONNEL: "Personal",
  INVESTMENT: "Investitionen", TAX: "Steuern", FINANCING: "Finanzierung", OTHER_COSTS: "Sonstige Ausgaben",
};
const REASON: Readonly<Record<ExpectedPaymentReason, string>> = {
  CUSTOMER_PROMISE: "Zusage des Kunden", INSTALMENT_AGREED: "Ratenzahlung vereinbart (letzte Rate)",
  DISPUTE: "Reklamation oder Klärung offen", DUNNING: "Im Mahnlauf", PAYMENT_RISK: "Zahlungsausfall droht", OTHER: "Sonstiges",
};
const GRANULARITY: Readonly<Record<LiquidityGranularity, string>> = {
  DAY: "Tag", WEEK: "Woche", MONTH: "Monat", MONTH_END: "Monatsende", FIVE_YEARS: "5 Jahre",
};
const SOURCE: Readonly<Record<LiquiditySource, string>> = {
  CASH_FLOWS: "Zahlungsströme", ACCOUNTS: "Konten", PLANNING_ITEMS: "Planpositionen", PAYER_DELAYS: "Zahlungsverzug",
  CONTRACTS: "Lieferverträge", ORDERS: "Aufträge", TAX: "Steuer", PARTIAL_PERIOD: "jüngster Teilzeitraum",
  FOREIGN_CURRENCY: "Fremdwährung (nicht enthalten)",
};
const PLANNING_KIND: Readonly<Record<PlanningItem["kind"], string>> = { REVENUE: "Einnahme", COST: "Ausgabe" };
const PLANNING_CATEGORY: Readonly<Record<PlanningItem["category"], string>> = { PERSONNEL: CATEGORY.PERSONNEL, INVESTMENT: CATEGORY.INVESTMENT, TAX: CATEGORY.TAX,
  FINANCING: CATEGORY.FINANCING, OTHER: CATEGORY.OTHER_COSTS };
const PLANNING_CYCLE: Readonly<Record<PlanningItem["cycle"], string>> = { ONCE: "Einmalig", MONTHLY: "Monatlich", QUARTERLY: "Vierteljährlich",
  HALF_YEARLY: "Halbjährlich", YEARLY: "Jährlich" };
const GROUP: Readonly<Record<PositionGroupKey, string>> = { ACCOUNT: "Konten", IN: "Einnahmen", OUT: "Ausgaben" };
const NOTE: Readonly<Record<PositionNote, string>> = {
  OVERDUE: "überfällig", PROJECTED: "fortgeschrieben", STALE_BALANCE: "Kontostand veraltet", BEYOND_FROZEN: "Stand endet vor dem Horizont",
};

export const LIQUIDITY_TEXT_WEB = {
  areaFinance: "Finanzen",
  tabLiquidity: "Liquidität",
  locked: "Die Liquiditätsplanung ist der Leitung und der Verwaltung vorbehalten.",
  loading: "Lädt …",
  loadFailed: "Die Liquidität ließ sich nicht laden.",
  jumpNote: "Aus der Liquidität geöffnet.",
  legendName: "Legende des Diagramms",
  hintsName: "Hinweise zur Liquidität",
  granularity: GRANULARITY,
  category: CATEGORY,
  reason: REASON,
  source: SOURCE,
  group: GROUP,
  note: { ...NOTE, sourceGone: "Quelle entfernt", scenarioOnly: "nur im Szenario",
    frozen: (at: string, to: string): string => `eingefroren am ${at}, Stand bis ${to}`, disabled: "nicht eingerechnet" },
  zone: {
    DETAIL: (end: string): string => `Tage bis ${end}`,
    QUARTERS: (year: string): string => `Quartale ${year}`,
    YEARS: (from: string, to: string): string => `Jahre ${from}–${to}`,
  },
  period: {
    week: (week: number, from: string, to: string): string => `KW ${week} · ${from}–${to}`,
    quarter: (quarter: number, year: string): string => `Q${quarter} ${year}`,
    quarterShort: (quarter: number): string => `Q${quarter}`,
  },
  warning: {
    belowMinimum: (amount: string, date: string, first: string): string =>
      `Tiefster Stand ${amount} am ${date} — unter dem Mindestsaldo ab ${first}`,
    lowest: (amount: string, date: string): string => `Tiefster Stand ${amount} am ${date}`,
    lowestInYears: (from: string, to: string, amount: string, date: string): string => `Tiefster Stand ${from}–${to}: ${amount} am ${date}`,
    overdueIn: (n: number, amount: string): string =>
      `${count(n, "überfälliger Eingang", "überfällige Eingänge")}, ${amount}, auf heute gelegt — Erwartungstermin setzen`,
    overdueOut: (n: number, amount: string): string =>
      `${count(n, "überfälliger Ausgang", "überfällige Ausgänge")}, ${amount}, auf heute gelegt`,
    staleBalance: (date: string, days: number): string => `Kontostand vom ${date} ist ${days} Tage alt`,
    noAccount: "Kein aktives Konto mit Kontostand — der Startsaldo zählt mit 0 €",
    withoutPlan: (n: number): string => `${count(n, "Auftrag", "Aufträge")} ohne Zahlplan`,
    beyond: (n: number, amount: string): string => `${n} Posten nach dem Horizont, ${amount}`,
    missing: (sources: string): string => `Reihe unvollständig: ${sources}`,
    /** Hinweis mit dem Namen seines Kontos oder Unternehmens davor. */
    named: (name: string, text: string): string => `${name}: ${text}`,
  },
  draft: {
    badge: (n: number): string => count(n, "Änderung", "Änderungen"),
    confirm: (name: string, c: DraftCounts): string => {
      const parts = [[c.off, "aus"], [c.on, "wieder an"], [c.frozen, "jetzt einfrieren"], [c.live, "wieder live"]] as const;
      const said = parts.filter(([n]) => n > 0).map(([n, what]) => `${positions(n)} ${what}`);
      return `Im Szenario ‚${name}‘ speichern: ${said.join(", ")}.`;
    },
    /** Der Symbolknopf am Telefon: sein Name. */
    phoneName: (n: number): string => `Entwurf: ${count(n, "Änderung", "Änderungen")}`,
    /** Sofort-Popup am Kennzeichen, wenn die Vorschau nicht mehr nachzieht. */
    previewLimit: (max: number): string => `Die Vorschau zeigt höchstens ${max} Umstellungen; gespeichert wird alles.`,
    save: "Speichern …",
    saveAsNew: "Als neues Szenario …",
    discard: "Verwerfen",
    confirmTitle: "Entwurf speichern?",
    confirmSave: "Speichern",
    saved: "Entwurf gespeichert",
    saveFailed: "Der Entwurf ließ sich nicht speichern.",
    conflict: "Das Szenario wurde inzwischen geändert. Dein Entwurf steht noch da; die Reihe zeigt ihn auf dem neuen Stand. „Speichern …“ schreibt ihn dorthin.",
    scenarioGone: (name: string): string => `Das gewählte Szenario gibt es nicht mehr, jetzt gilt ‚${name}‘. Dein Entwurf steht noch da und ginge mit „Speichern …“ dorthin. „Als neues Szenario …“ sichert ihn, ohne ‚${name}‘ zu ändern.`,
    tooMany: (n: number, max: number): string => `Höchstens ${max} Änderungen lassen sich auf einmal speichern, der Entwurf hat ${n}. Bitte einzelne Schalter zurückstellen.`,
  },
  scenario: {
    menu: { create: "Neues Szenario …", duplicate: "Duplizieren …", edit: "Bearbeiten …", baseline: "Als Basis festlegen", delete: "Löschen" },
    title: { create: "Neues Szenario", duplicate: "Szenario duplizieren", edit: "Szenario bearbeiten", fromDraft: "Als neues Szenario" },
    name: "Name",
    description: "Beschreibung",
    minimumBalance: "Mindestsaldo",
    minimumBalanceHint: "Leer lassen entfernt den Mindestsaldo. Negative Werte sind erlaubt (Kontokorrent).",
    copyContent: "Abweichungen und eigene Planpositionen übernehmen",
    copyName: (name: string): string => `${name} (Kopie)`,
    fromDraftHint: (n: number): string => `Übernimmt ${count(n, "Änderung", "Änderungen")} aus dem Entwurf.`,
    saved: "Szenario gespeichert",
    baselineTitle: "Als Basis festlegen?",
    baselineText: (name: string): string => `„${name}“ wird die Basis des Unternehmens. Die bisherige Basis bleibt als Szenario erhalten.`,
    baselineConfirm: "Als Basis festlegen",
    baselineSaved: "Basis festgelegt",
    deleteTitle: "Szenario löschen?",
    deleteText: (name: string, withDraft: boolean): string =>
      `„${name}“ entfällt mit seinen Abweichungen und eigenen Planpositionen.${withDraft ? " Der ungespeicherte Entwurf entfällt ebenfalls." : ""}`,
    deleteConfirm: "Löschen",
    deleted: "Szenario gelöscht",
    actionFailed: "Das Szenario ließ sich nicht ändern.",
    conflict: "Das Szenario wurde inzwischen geändert. Der Stand ist neu geladen, bitte noch einmal wählen.",
  },
  toolbar: {
    label: "Werkzeuge der Liquidität",
    companies: "Unternehmen",
    scenario: "Szenario",
    baseline: "Basis",
    /** Der volle Satz (593) steht im Sofort-Popup an den Menüs, sichtbar steht `baselinePerCompanyShort`: Mehr passt nicht hinein. */
    baselinePerCompany: "Basis je Unternehmen",
    baselinePerCompanyShort: "je Unternehmen",
    scenarioMenu: "Weitere Aktionen zum Szenario",
    scenarioMenuMark: "…",
    minimumBalanceLocked: "Das Unternehmen hat noch keine Basis. Sie entsteht beim ersten Speichern, zum Beispiel mit dem ersten Konto.",
    compare: "Vergleich mit",
    compareNone: "ohne",
    granularity: "Verdichtung",
    positions: (n: number): string => `Positionen (${n})`,
    backToContent: "Zurück zur Liquidität",
  },
  range: {
    label: "Zeitraum",
    all: "Gesamt",
    earlier: "Früher",
    later: "Später",
    frame: "Zeitraum aufziehen",
    free: "frei",
  },
  positions: {
    search: "Positionen suchen",
    empty: "Keine Position gefunden",
    next: (date: string, amount: string): string => `nächster Termin ${date}: ${amount}`,
    noBalance: "noch kein Kontostand",
    balanceOf: (amount: string, date: string): string => `${amount} vom ${date}`,
    addAccount: "Konto anlegen …",
    addBalance: "Kontostand erfassen …",
    /** Name des „⋯“ eines Kontos (Anatomie Teil 15). */
    accountActionsOf: (name: string): string => `Aktionen zu Konto ${name}`,
    inactive: "inaktiv",
    loadFailed: "Die Posten ließen sich nicht laden.",
    expectedAt: (date: string): string => `erwartet ${date}`,
    dueAt: (date: string): string => `fällig ${date}`,
    balances: "Kontostände",
    noPosts: "Keine Posten im Horizont",
    enabledOf: (label: string): string => `${label} einrechnen`,
    freezeOf: (label: string): string => `${label} einfrieren`,
    freezeOnSave: "wird beim Speichern eingefroren",
    liveOnSave: "wird beim Speichern wieder live",
    /** Hinweis-Kennzeichen einer Position, die der Entwurf umstellt (PF18). */
    changedInDraft: "im Entwurf geändert",
  },
  table: {
    label: "Liquidität je Periode",
    period: "Periode",
    in: "Einnahmen",
    out: "Ausgaben",
    net: "Saldo der Periode",
    closing: "Stand am Ende",
    lowest: "Tiefststand",
    lowestAt: (amount: string, date: string): string => `${amount} am ${date}`,
    compareClosing: "Stand Vergleich",
    delta: "Δ",
    change: "Veränderung",
    closingAtPeriodEnd: "Stand am Periodenende",
    anchor: (date: string): string => `Ausgangsstand ${date}`,
    belowMinimum: "unter Mindestsaldo",
  },
  legend: { balance: "Saldo", compare: "Vergleich", minimum: "Mindestsaldo" },
  minimumLine: (amount: string): string => `Mindestsaldo ${amount}`,
  now: "jetzt",
  chartLabel: (end: string, amount: string, date: string): string => `Liquidität bis ${end}, tiefster Stand ${amount} am ${date}`,
  chartLabelYears: (end: string, from: string, to: string, amount: string, date: string): string =>
    `Liquidität bis ${end}, tiefster Stand ${from}–${to}: ${amount} am ${date}`,
  popup: {
    category: (name: string, amount: string): string => `${name}: ${amount}`,
    closing: (amount: string): string => `Stand am Ende ${amount}`,
    lowest: (amount: string, date: string): string => `Tiefststand ${amount} am ${date}`,
    balance: (amount: string): string => `Stand ${amount}`,
    compare: (amount: string): string => `Vergleich ${amount}`,
    delta: (amount: string): string => `Δ ${amount}`,
  },
  expectedPayment: {
    title: (invoiceNumber: string | null): string => (invoiceNumber === null ? EXPECTED_PAYMENT : `${EXPECTED_PAYMENT} · ${invoiceNumber}`),
    line: EXPECTED_PAYMENT,
    lineSet: (date: string, reason: string): string => `${date} · ${reason}`,
    lineDefault: (date: string): string => `${date} (Vorgabe)`,
    change: "Ändern …",
    changeName: `${EXPECTED_PAYMENT} ändern …`,
    /** Eintrag im „⋯“ des Postens einer offenen Rechnung (115). */
    changeFromPost: "Erwartungstermin ändern …",
    postActionsOf: (reference: string | null): string => (reference === null ? "Aktionen zur Rechnung" : `Aktionen zu Rechnung ${reference}`),
    loadFailed: "Der Erwartungstermin ließ sich nicht laden.",
    notOpen: "Diese Rechnung ist nicht mehr offen. Der Erwartungstermin lässt sich nicht mehr ändern.",
    close: "Schließen",
    payer: "Zahler",
    invoiceDate: "Rechnungsdatum",
    dueDate: "Fällig am",
    open: "Offen",
    noValue: "—",
    defaultDerived: (date: string, due: string, days: number, n: number): string =>
      `Vorgabe ${date} — fällig ${due} + ${count(days, "Tag", "Tage")} mittlerer Verzug aus ${paidInvoices(n)}`,
    defaultNoHistory: (date: string): string => `Vorgabe ${date} — ohne Zahlungshistorie: Fälligkeit`,
    defaultNoDelay: (date: string, n: number): string => `Vorgabe ${date} — Fälligkeit; aus ${paidInvoices(n)} ergibt sich kein Verzug`,
    defaultNoDue: (date: string): string => `Vorgabe ${date} — ohne Fälligkeit: heute`,
    date: "Erwartet am",
    reason: "Grund",
    chooseReason: "Bitte wählen",
    note: "Bemerkung",
    dateInPast: "Der Termin darf nicht vor heute liegen.",
    reasonRequired: "Bitte einen Grund wählen.",
    noteRequired: "Bei „Sonstiges“ ist eine Bemerkung nötig.",
    reset: "Auf Vorgabe zurücksetzen",
    resetTitle: "Auf Vorgabe zurücksetzen?",
    resetConfirm: "Zurücksetzen",
    resetText: (date: string): string => `Der gesetzte Termin entfällt, es gilt wieder die Vorgabe ${date}.`,
    history: "Verlauf",
    historyHead: (at: string, by: string | null): string => (by === null ? at : `${at} · ${by}`),
    historyDefault: (date: string): string => `Vorgabe ${date}`,
    historyChange: (from: string, to: string): string => `${from} → ${to}`,
    noteChanged: "Bemerkung geändert",
    conflictBy: (name: string): string => `${name} hat den Erwartungstermin inzwischen geändert.`,
    conflictAnonymous: "Der Erwartungstermin wurde inzwischen geändert.",
    conflictSet: (state: string): string => `Aktuell: ${state}.`,
    conflictDefault: (date: string): string => `Aktuell gilt die Vorgabe ${date}.`,
    conflictChoice: "Deine Eingaben stehen noch da: „Neu laden“ übernimmt den aktuellen Stand, „Meine Eingaben behalten“ überschreibt ihn beim nächsten Speichern.",
    keepInputs: "Meine Eingaben behalten",
    saved: (set: boolean): string => (set ? "Erwartungstermin gespeichert" : "Erwartungstermin auf die Vorgabe zurückgesetzt"),
  },
  planningItem: {
    titleNew: "Neue Planposition",
    titleEdit: "Planposition ändern",
    add: "Neue Planposition …",
    addTo: (group: string): string => `Neue Planposition … ${group}`,
    actionsOf: (label: string): string => `Aktionen zu Planposition ${label}`,
    kind: "Art",
    kinds: PLANNING_KIND,
    category: "Kategorie",
    categories: PLANNING_CATEGORY,
    chooseCategory: "Bitte wählen",
    categoryRequired: "Bitte eine Kategorie wählen.",
    label: "Bezeichnung",
    labelRequired: "Bitte eine Bezeichnung angeben.",
    cycle: "Rhythmus",
    cycles: PLANNING_CYCLE,
    firstDue: "Erste Zahlung",
    dueDay: "Zahltag",
    dueDayHint: "31 = Monatsletzter",
    dueDayInvalid: "Bitte einen Tag von 1 bis 31 angeben.",
    amount: "Betrag je Zahlung",
    end: "Ende",
    endHint: "nur bei definitivem Ende; Pause = Betrag 0",
    scenarioOnly: (name: string): string => `nur im Szenario ‹${name}›`,
    note: "Bemerkung",
    saved: "Planposition gespeichert",
    delete: "Planposition löschen …",
    deleteTitle: "Planposition löschen?",
    deleteConfirm: "Löschen",
    deleteText: (label: string): string => `„${label}“ entfällt mit allen Beträgen aus der Planung.`,
    deleted: "Planposition gelöscht",
    amounts: "Beträge",
    amountFrom: (date: string): string => `ab ${date}`,
    pause: "Pause",
    changeAmount: "Betrag ändern ab …",
    amountDate: "Gilt ab",
    amountValue: "Betrag",
    amountNote: "Bemerkung",
    replaces: (amount: string, date: string): string => `ersetzt den Betrag ${amount} ab ${date}`,
    replaceTitle: "Betrag ersetzen?",
    replaceConfirm: "Ersetzen",
    amountSaved: "Betrag gespeichert",
    deleteAmountOf: (date: string): string => `Betrag ab ${date} löschen`,
    deleteAmountTitle: "Betrag löschen?",
    deleteAmountConfirm: "Löschen",
    deleteAmountText: (amount: string, date: string): string => `Der Betrag ${amount} ab ${date} entfällt; ab dann gilt wieder der vorige.`,
    amountDeleted: "Betrag gelöscht",
    loadFailed: "Die Planposition ließ sich nicht laden.",
    gone: "Diese Planposition gibt es nicht mehr.",
  },
  vat: {
    line: (month: string, amount: string, due: string): string => `Geschätzte USt-Zahllast ${month}: ${amount}, fällig ${due}`,
    surplus: (month: string, amount: string): string => `Geschätzter Vorsteuerüberhang ${month}: ${amount}`,
    withoutFrozen: (line: string): string => `${line} (ohne eingefrorene Positionen)`,
    more: (n: number): string => count(n, "weiterer Monat", "weitere Monate"),
    perCompany: "Die geschätzte USt-Zahllast steht nur in der Sicht eines einzelnen Unternehmens.",
  },
  dialogs: {
    save: "Speichern",
    euroUnit: "€",
    amountInvalid: "Bitte einen Betrag angeben, etwa 1.234,56.",
    dateRequired: "Bitte ein Datum angeben.",
    nameRequired: "Bitte einen Namen angeben.",
    changedElsewhere: "Der Stand wurde inzwischen geändert. Deine Eingaben stehen noch da; „Neu laden“ übernimmt den aktuellen Stand.",
    account: {
      titleNew: "Konto anlegen",
      titleEdit: "Konto ändern",
      company: "Unternehmen",
      currencyDefault: "EUR",
      name: "Name",
      iban: "IBAN",
      currency: "Währung",
      active: "Aktiv",
      saved: "Konto gespeichert",
    },
    balance: {
      title: "Kontostand erfassen",
      date: "Stand am",
      amount: "Betrag",
      note: "Bemerkung",
      replaces: (amount: string, date: string): string => `ersetzt den Stand ${amount} vom ${date}`,
      replaceTitle: "Kontostand ersetzen?",
      replaceConfirm: "Ersetzen",
      saved: "Kontostand gespeichert",
    },
  },
} as const;
